import { usePuckTheme } from "@/context/cms-puck/PuckThemeContext";
import {
  normalizePageAppearance,
  pageThemeCssVars,
  resolvePageAppearanceStyle,
  type PageAppearance,
} from "@/lib/cms-puck/blocks/appearance";
import {
  normalizePageCustomCss,
  sanitizePageCustomCss,
} from "@/lib/cms-puck/blocks/page-custom-css";
import {
  normalizePagePadding,
  resolvePagePaddingStyle,
  type PagePadding,
} from "@/lib/cms-puck/blocks/page-padding";

/**
 * Aperçu du canvas : applique fond/texte/police résolus (sémantique front) au
 * conteneur de la page éditée. Lit le thème via le contexte de l'éditeur.
 */
export function RootPreview({
  appearance,
  padding,
  customCss,
  children,
}: {
  appearance: PageAppearance | undefined;
  padding?: PagePadding | undefined;
  customCss?: string | null;
  children: React.ReactNode;
}) {
  const theme = usePuckTheme();
  const style = resolvePageAppearanceStyle(
    theme,
    normalizePageAppearance(appearance),
  );
  const paddingStyle = resolvePagePaddingStyle(normalizePagePadding(padding));
  const sanitizedCss = sanitizePageCustomCss(normalizePageCustomCss(customCss));
  // `cms-page-theme-scope` + variables `--cms-page-*` : MIROIR du front
  // (server-providers.tsx). Avec cms-canvas.css, la typo/poids/interligne du thème
  // cascade au contenu comme sur le site public (WYSIWYG).
  const fontFamily = `'${style.fontFamily}', sans-serif`;
  return (
    <div
      className="min-h-full cms-page-theme-scope"
      style={
        {
          backgroundColor: style.backgroundColor,
          color: style.color,
          fontFamily,
          fontWeight: style.fontWeight,
          ...paddingStyle,
          // Lissage de police IDENTIQUE au site public (`<body class="antialiased">`,
          // soit grayscale). Le canvas Puck est une iframe qui n'hérite PAS du
          // `font-smoothing` du body du BO → sans ça le texte y est plus gras /
          // saturé que sur le front (cf. parité WYSIWYG).
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
          ...pageThemeCssVars(theme, style),
          "--cms-page-font-family": fontFamily,
          "--cms-page-font-weight": String(style.fontWeight),
        } as React.CSSProperties
      }
    >
      {sanitizedCss ? (
        <style
          data-cms-page-custom-css=""
          dangerouslySetInnerHTML={{ __html: sanitizedCss }}
        />
      ) : null}
      {children}
    </div>
  );
}
